import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2, Star, X } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { ViolationBadge } from "@/components/audit/ViolationBadge";
import { AmazonReportText } from "@/components/customer/AmazonReportText";
import { CopyReviewUrlButton } from "@/components/customer/CopyReviewUrlButton";
import { TakedownStatusControls } from "@/components/customer/TakedownStatusControls";
import { api } from "@/lib/api";
import {
  amazonTakedownCapability,
  canAuthorizeManagedTakedowns,
  type BrandAmazonAccess,
} from "@/lib/amazonAccess";

type BrandReview = {
  id: string;
  asin: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  productTitle: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  createdAt: string;
  classification: {
    violations:
      | { type: string; policyClause?: string; evidence?: string }[]
      | null;
    rationale: string | null;
    takedownRequest: string | null;
    actionStatus: string;
    submittedAt: string | null;
    removedAt: string | null;
    chargeStatus?: string;
  } | null;
};

type SeverityFilter = "all" | "high" | "low";
type StatusFilter = "all" | "none" | "authorized" | "submitted" | "removed" | "rejected";
type StatusUpdate = {
  actionStatus: string;
  submittedAt: string | null;
  removedAt: string | null;
  chargeStatus?: string | null;
};

function reviewSnippet(review: BrandReview): string {
  const title = review.title?.trim();
  const body = review.body.trim();
  if (!title) return truncate(body);

  const normalizedTitle = title.toLowerCase();
  const normalizedBody = body.toLowerCase();
  if (normalizedBody === normalizedTitle) return `"${title}"`;
  if (normalizedBody.startsWith(`${normalizedTitle} `)) {
    return `"${title}" — ${truncate(body.slice(title.length).trim())}`;
  }
  return `"${title}" — ${truncate(body)}`;
}

function truncate(value: string): string {
  return value.length > 140 ? `${value.slice(0, 140)}…` : value;
}

export default function DashboardReviews() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const brandId = ctx.activeBrandId;
  const activeBrand = ctx.brands.find((brand) => brand.id === brandId) ?? null;
  const [reviews, setReviews] = useState<BrandReview[] | null>(null);
  const [open, setOpen] = useState<BrandReview | null>(null);
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [statusF, setStatusF] = useState<StatusFilter>("all");

  function applyStatus(reviewId: string, next: StatusUpdate) {
    const updateReview = (review: BrandReview): BrandReview =>
      review.id === reviewId && review.classification
        ? {
            ...review,
            classification: {
              ...review.classification,
              actionStatus: next.actionStatus,
              submittedAt: next.submittedAt,
              removedAt: next.removedAt,
              ...(next.chargeStatus !== undefined
                ? { chargeStatus: next.chargeStatus ?? undefined }
                : {}),
            },
          }
        : review;

    setReviews((prev) => (prev ? prev.map(updateReview) : prev));
    setOpen((prev) => (prev ? updateReview(prev) : prev));
  }

  useEffect(() => {
    if (!brandId) {
      setReviews([]);
      return;
    }
    let cancelled = false;
    setReviews(null);
    api<{ reviews: BrandReview[] }>(`/customer/brands/${brandId}/reviews`).then(
      (res) => {
        if (cancelled) return;
        setReviews(res.data?.reviews ?? []);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  const filtered = useMemo(() => {
    if (!reviews) return null;
    return reviews.filter((r) => {
      if (statusF !== "all") {
        const s = r.classification?.actionStatus ?? "none";
        if (statusF === "none" && s !== "none" && s !== "watching") return false;
        if (statusF !== "none" && s !== statusF) return false;
      }
      if (severity !== "all") {
        const v = r.classification?.violations?.length ?? 0;
        const high = v >= 2 || r.rating <= 2;
        if (severity === "high" && !high) return false;
        if (severity === "low" && high) return false;
      }
      return true;
    });
  }, [reviews, severity, statusF]);

  if (ctx.brands.length === 0) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          No brands yet
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Add a brand to start surfacing flagged reviews.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          + Add a brand
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Flagged reviews
      </h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Reviews we've flagged as policy violations across your catalog.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Brand
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ctx.brands.map((b) => {
            const active = b.id === brandId;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => ctx.onBrandChange(b.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                  active
                    ? "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                {b.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <FilterChip
          label="Severity"
          value={severity}
          options={[
            { v: "all", label: "All" },
            { v: "high", label: "High" },
            { v: "low", label: "Low" },
          ]}
          onChange={(v) => setSeverity(v as SeverityFilter)}
        />
        <FilterChip
          label="Action"
          value={statusF}
          options={[
            { v: "all", label: "All" },
            { v: "none", label: "Awaiting" },
            { v: "authorized", label: "Awaiting filing" },
            { v: "submitted", label: "Submitted" },
            { v: "removed", label: "Removed" },
            { v: "rejected", label: "Rejected" },
          ]}
          onChange={(v) => setStatusF(v as StatusFilter)}
        />
      </div>

      <div className="mt-6 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {filtered === null && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {filtered && filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-slate-600 dark:text-slate-400">
            Nothing flagged matches these filters.
          </div>
        )}
        {filtered?.map((r) => (
          <ReviewRow key={r.id} review={r} onOpen={() => setOpen(r)} />
        ))}
      </div>

      {open && (
        <ReviewModal
          review={open}
          brand={activeBrand}
          onClose={() => setOpen(null)}
          onStatusChange={applyStatus}
        />
      )}
    </div>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { v: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <div className="flex gap-1">
        {options.map((o) => {
          const active = o.v === value;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                active
                  ? "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  none: {
    label: "Awaiting authorization",
    cls: "bg-slate-50 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  },
  watching: {
    label: "Watching",
    cls: "bg-slate-50 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  },
  authorized: {
    label: "Authorized · awaiting filing",
    cls: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  },
  submitted: {
    label: "Submitted",
    cls: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  },
  removed: {
    label: "Removed",
    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900",
  },
  rejected: {
    label: "Rejected",
    cls: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
  },
};

function ReviewRow({ review, onOpen }: { review: BrandReview; onOpen: () => void }) {
  const status = review.classification?.actionStatus ?? "none";
  const meta = STATUS_PILL[status] ?? STATUS_PILL.none;
  const violations = review.classification?.violations ?? [];
  function onRowKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onRowKeyDown}
      className="flex w-full cursor-pointer items-start gap-4 px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-slate-800/60"
    >
      <div className="flex w-32 shrink-0 flex-col items-start gap-1">
        <StarRating rating={review.rating} />
        <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
          {review.asin}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {review.productTitle || review.asin}
          </span>
          {review.reviewUrl && (
            <span className="inline-flex items-center gap-1">
              <a
                href={review.reviewUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              >
                Amazon review <ExternalLink className="h-3 w-3" />
              </a>
              <CopyReviewUrlButton url={review.reviewUrl} />
            </span>
          )}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {review.verifiedPurchase ? "Verified" : "Unverified"}
            {review.reviewerName ? ` · ${review.reviewerName}` : ""}
          </span>
        </div>
        <p className="mt-1 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">
          {reviewSnippet(review)}
        </p>
        {violations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {violations.map((v, i) => (
              <ViolationBadge key={i} type={v.type} />
            ))}
          </div>
        )}
      </div>
      <div className="ml-2 flex shrink-0 flex-col items-end gap-1">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
        >
          {meta.label}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {new Date(review.createdAt).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

function ReviewModal({
  review,
  brand,
  onClose,
  onStatusChange,
}: {
  review: BrandReview;
  brand: BrandAmazonAccess | null;
  onClose: () => void;
  onStatusChange: (reviewId: string, next: StatusUpdate) => void;
}) {
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = review.classification?.actionStatus ?? "none";
  const awaitingAuthorization = status === "none" || status === "watching";
  const takedownsReady = Boolean(
    brand && canAuthorizeManagedTakedowns(brand)
  );
  const canAuthorize = awaitingAuthorization && takedownsReady;

  async function authorize() {
    setError(null);
    setAuthorizing(true);
    const res = await api<{ ok: true; status: string }>(
      `/customer/reviews/${review.id}/authorize-takedown`,
      {
        method: "POST",
        body: JSON.stringify({ brandId: brand?.id }),
      }
    );
    setAuthorizing(false);
    if (!res.ok) {
      setError(res.error ?? "Couldn't authorize.");
      return;
    }
    onStatusChange(review.id, {
      actionStatus: "authorized",
      submittedAt: null,
      removedAt: null,
      chargeStatus: "not_billable",
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 pb-6 pt-5">
          <div className="flex items-center gap-3">
            <StarRating rating={review.rating} />
            <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
              {review.asin}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {new Date(review.createdAt).toLocaleDateString()}
            </span>
          </div>
          <h2 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {review.title || review.productTitle || "Flagged review"}
          </h2>
          {review.reviewUrl && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <a
                href={review.reviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              >
                Open Amazon review <ExternalLink className="h-3 w-3" />
              </a>
              <CopyReviewUrlButton url={review.reviewUrl} />
            </div>
          )}
          <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
            {review.body}
          </p>

          {review.classification?.rationale && (
            <div className="mt-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Why we flagged this
              </div>
              <p className="mt-1.5 text-sm text-slate-700 dark:text-slate-300">
                {review.classification.rationale}
              </p>
            </div>
          )}

          {review.classification && takedownsReady && (
            <AmazonReportText review={review} />
          )}

          <div className="mt-6 border-t border-slate-100 pt-5 dark:border-slate-800">
            {canAuthorize && (
              <div>
                <button
                  type="button"
                  onClick={authorize}
                  disabled={authorizing}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                >
                  {authorizing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {authorizing ? "Authorizing…" : "Authorize takedown"}
                </button>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Included with your monitoring plan.
                </p>
                {error && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-3.5 w-3.5" /> {error}
                  </p>
                )}
              </div>
            )}
            {awaitingAuthorization && !takedownsReady && (
              <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900">
                <p>
                  {brand &&
                  amazonTakedownCapability(brand) ===
                    "unverified_no_brand_registry"
                    ? "Managed takedowns are not yet supported for this brand without Brand Registry."
                    : "Complete Brand Registry setup before authorizing managed takedowns."}
                </p>
                {brand && (
                  <Link
                    to={`/dashboard/onboarding?brand=${encodeURIComponent(brand.id)}`}
                    className="mt-2 inline-flex font-medium underline underline-offset-4"
                  >
                    View Amazon setup
                  </Link>
                )}
              </div>
            )}
            {!awaitingAuthorization && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {status === "authorized"
                  ? "Authorized — waiting for Reputely to file it through Brand Registry Support."
                  : status === "submitted"
                    ? "Submitted to Amazon — awaiting their response."
                    : "No further action is available for this review."}
              </p>
            )}
            {review.classification &&
              brand &&
              status === "authorized" && (
              <TakedownStatusControls
                reviewId={review.id}
                actionStatus={status}
                onChange={(next) => onStatusChange(review.id, next)}
                className={canAuthorize ? "mt-4" : "mt-3"}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe
              ? "fill-amber-400 text-amber-400"
              : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"
          }`}
        />
      ))}
    </span>
  );
}
